import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Fixture } from '../types'
import { apiClient } from '../utils/api'

interface BlogFixtureStore {
  fixtures: Fixture[]
  fetchFixtures: () => Promise<void>
  createFixture: (fixture: Fixture) => Promise<void>
  updateFixture: (fixture: Fixture) => Promise<void>
  deleteFixture: (fixtureId: string) => Promise<void>
}

const sampleFixtureKeys = new Set([
  'NPFL|Kano Pillars|Enyimba FC|Kano',
  'NPFL|Rivers United|Akwa United|Port Harcourt',
  'NPFL|Enyimba FC|Plateau United|Aba',
  'NLO|Nasarawa United|Gombe United|Lafia',
  'NPFL|Shooting Stars|Lobi Stars|Ibadan',
])

const removeSampleFixtures = (fixtures: Fixture[]) => fixtures.filter((fixture) =>
  !sampleFixtureKeys.has(`${fixture.competition}|${fixture.home_team}|${fixture.away_team}|${fixture.location}`)
)

const readLegacyFixtures = (): Fixture[] => {
  try {
    const savedBlogState = localStorage.getItem('scoutang-blog-store')
    if (!savedBlogState) return []

    const state = JSON.parse(savedBlogState).state
    return Array.isArray(state?.fixtures) ? removeSampleFixtures(state.fixtures as Fixture[]) : []
  } catch {
    return []
  }
}

const legacyFixtures = readLegacyFixtures()

export const useBlogFixtureStore = create<BlogFixtureStore>()(
  persist(
    (set, get) => ({
      fixtures: legacyFixtures,
      fetchFixtures: async () => {
        try {
          const remoteFixtures = await apiClient.getBlogFixtures() as Fixture[]
          const remoteIds = new Set(remoteFixtures.map((fixture) => fixture.id))
          const cachedOnlyFixtures = get().fixtures.filter((fixture) => !remoteIds.has(fixture.id))

          if (cachedOnlyFixtures.length > 0) {
            try {
              const synced = await apiClient.syncBlogFixtures(cachedOnlyFixtures) as Fixture[]
              const syncedIds = new Set(synced.map((fixture) => fixture.id))
              const stillLocal = cachedOnlyFixtures.filter((fixture) => !syncedIds.has(fixture.id))
              set({ fixtures: [...remoteFixtures, ...synced, ...stillLocal] })
              return
            } catch (error) {
              console.error('Failed to sync locally saved blog fixtures:', error)
            }
          }

          set({ fixtures: [...remoteFixtures, ...cachedOnlyFixtures] })
        } catch (error) {
          console.error('Failed to fetch blog fixtures:', error)
        }
      },
      createFixture: async (fixture) => {
        const created = await apiClient.createBlogFixture(fixture)
        if (!created?.id) throw new Error('Supabase did not return the saved blog fixture')
        set((state) => ({ fixtures: [created as Fixture, ...state.fixtures.filter((item) => item.id !== fixture.id)] }))
      },
      updateFixture: async (fixture) => {
        const { id, ...updates } = fixture
        const updated = await apiClient.updateBlogFixture(id, updates)
        if (!updated?.id) throw new Error('Blog fixture was not updated in Supabase')
        set((state) => ({ fixtures: state.fixtures.map((item) => item.id === id ? updated as Fixture : item) }))
      },
      deleteFixture: async (fixtureId) => {
        await apiClient.deleteBlogFixture(fixtureId)
        set((state) => ({ fixtures: state.fixtures.filter((fixture) => fixture.id !== fixtureId) }))
      },
    }),
    {
      name: 'scoutang-fixture-store',
      version: 1,
      migrate: (persistedState: unknown) => {
        const state = persistedState as Partial<BlogFixtureStore>
        return {
          fixtures: Array.isArray(state.fixtures) ? removeSampleFixtures(state.fixtures) : [],
        }
      },
    }
  )
)